import React, { useState } from "react";

import { MoreIcon, Typography } from "@packages/ds";
import { FormattedMessage } from "react-intl";

import { ACTIVITY_TYPES } from "Constants/constants";
import * as Dropdown from "ds/Dropdown";
import logger from "Libs/logger";
import { roundedNumber } from "Libs/utils";
import * as A from "src/organization/common/components/activities/Activity/Activity.styles";
import ActivityDate from "src/organization/common/components/activities/Activity/ActivityDate";
import BackupDeleteModal from "src/organization/common/components/activities/Activity/Modals/BackupDeleteModal";
import BackupRestoreModal from "src/organization/common/components/activities/Activity/Modals/BackupRestoreModal";
import activityTemplates from "src/organization/common/components/activities/activityTemplates";

import * as S from "./Backup.style";

import type { EnvironmentBackup } from "@packages/client";

const activityTemplate = activityTemplates[ACTIVITY_TYPES.ENVIRONMENT.BACKUP];

interface BackupProps {
  className?: string;
  backup: EnvironmentBackup;
  parent: string;
  name: string;
  projectId: string;
  environmentId: string;
  organizationId: string;
  getAllBackups: () => void;
  isCustomBackupSchedulesEnabled?: boolean;
}

const Backup = ({
  className = "",
  backup,
  parent,
  name,
  projectId,
  environmentId,
  organizationId,
  getAllBackups,
  isCustomBackupSchedulesEnabled
}: BackupProps) => {
  const [showRestoreModal, setShowRestoreModal] = useState<boolean>(false);
  const [showDeleteModal, setShowDeleteModal] = useState<boolean>(false);

  const { created_at, expires_at, id, restorable, automated } = backup;

  const handleRestore = async () => {
    try {
      await backup.restore({
        environment_name: name,
        branch_from: parent || name, // If the branch is production, there won't be a parent branch. Instead, branch against itself.
        restore_code: true
      });
    } catch (error) {
      logger(
        {
          errMessage: error,
          environmentId: name
        },
        {
          action: "BACKUP_RESTORE"
        }
      );
    }
  };

  const handleDelete = async () => {
    try {
      await backup.delete();
    } catch (error) {
      logger(
        {
          errMessage: error,
          environmentId: name
        },
        {
          action: "BACKUP_DELETE"
        }
      );
    } finally {
      getAllBackups();
    }
  };

  const hasDeleteLink = backup.hasLink("#delete");
  const shouldRenderRestore = restorable && backup.hasLink("#restore");
  const shouldRenderDropdown = hasDeleteLink || shouldRenderRestore;
  const isDeletingStatus = backup.status === "DELETING";

  const backupSize = backup?.size_of_volumes;

  return (
    <A.ActivityListItemLayout
      className={className}
      isDeletingStatus={isDeletingStatus}
    >
      <A.Wrapper>
        <S.Content
          isCustomBackupSchedulesEnabled={!!isCustomBackupSchedulesEnabled}
        >
          <A.Icon>{activityTemplate.icon}</A.Icon>
          <S.TextWrapper>ID:&nbsp;{id}</S.TextWrapper>
          {isCustomBackupSchedulesEnabled && (
            <>
              <Typography.Label $color="neutral-600">
                <FormattedMessage
                  id="environment.backups.backupSize"
                  values={{
                    backupSize: roundedNumber((backupSize ?? 0) / 1024)
                  }}
                />
              </Typography.Label>
            </>
          )}

          <S.EndContent>
            {automated ? (
              <A.ActivityDate data-testid="backups_expires">
                Exp.&nbsp;
                <ActivityDate date={`${expires_at}`} />
              </A.ActivityDate>
            ) : null}

            <A.ActivityDate>
              <ActivityDate date={created_at} />
            </A.ActivityDate>
            {shouldRenderDropdown && (
              <Dropdown.Root id="backups">
                <Dropdown.Trigger>
                  <MoreIcon aria-label="backup-more" />
                </Dropdown.Trigger>
                <Dropdown.Portal>
                  <Dropdown.Content align="end">
                    {shouldRenderRestore && (
                      <Dropdown.Item
                        data-testid="backups_restore"
                        onClick={() => setShowRestoreModal(true)}
                      >
                        <FormattedMessage
                          id="activity.restore_button"
                          defaultMessage="Restore"
                        />
                      </Dropdown.Item>
                    )}

                    {hasDeleteLink ? (
                      <Dropdown.Item
                        data-testid="backups_delete"
                        onClick={() => setShowDeleteModal(true)}
                      >
                        <FormattedMessage
                          id="backup.delete_button"
                          defaultMessage="Delete"
                        />
                      </Dropdown.Item>
                    ) : null}
                  </Dropdown.Content>
                </Dropdown.Portal>
              </Dropdown.Root>
            )}
          </S.EndContent>
        </S.Content>
      </A.Wrapper>
      {shouldRenderRestore && (
        <BackupRestoreModal
          show={showRestoreModal}
          setShow={setShowRestoreModal}
          backup={backup}
          handleRestore={handleRestore}
          projectId={projectId}
          environmentId={environmentId}
          organizationId={organizationId}
          shouldRedirect={true}
          activity={undefined}
        />
      )}

      {hasDeleteLink && (
        <BackupDeleteModal
          show={showDeleteModal}
          setShow={setShowDeleteModal}
          backup={backup}
          handleDelete={handleDelete}
          automated={automated}
        />
      )}
    </A.ActivityListItemLayout>
  );
};

export default Backup;
